import { ActualityHqWithCongestionsRow } from '@warebee/shared/dataset-queries';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
  EVENT_GROUPING_CONFIG,
  EVENT_ISSUES_CONFIG,
  EVENT_STATS_CONFIG,
  EventGrouping,
  EventIssueTiming,
  EventStatCategory,
  getCategoriesForGrouping,
  getIssuesByTiming,
  getIssuesForGrouping,
  getStatsByCategory,
  getStatsForGrouping,
} from '../constants/actuality.event.config';
import { actualityVisibleEventIssuesSelector } from '../store/actuality.state';

export interface ProcessedEventIssue {
  key: string;
  title: string;
  value: string;
  unitOfMeasure?: string;
  timing: EventIssueTiming;
  severity: 'info' | 'warning' | 'error';
}

export interface ProcessedEventStat {
  key: string;
  title: string;
  value: string | number;
  unitOfMeasure?: string;
  category: EventStatCategory;
  defaultVisible: boolean;
  canCopy?: boolean;
  truncate?: boolean;
}

/**
 * Hook to get processed issues for an event based on grouping
 */
export const useEventIssues = (
  event: ActualityHqWithCongestionsRow,
  grouping: EventGrouping = 'all',
  timing?: EventIssueTiming,
  visibleIssues?: string[],
) => {
  const { t } = useTranslation('feed');
  const defaultVisibleIssues = useRecoilValue(
    actualityVisibleEventIssuesSelector,
  );

  return useMemo(() => {
    let issuesConfig = timing
      ? getIssuesByTiming(timing, grouping)
      : getIssuesForGrouping(grouping);

    // Filter by visibility if provided, otherwise use defaults
    const issuesToShow = visibleIssues || defaultVisibleIssues;
    if (issuesToShow.length > 0) {
      issuesConfig = issuesConfig.filter(config =>
        issuesToShow.includes(config.key),
      );
    }

    const processedIssues: ProcessedEventIssue[] = [];

    issuesConfig.forEach(config => {
      if (config.condition(event)) {
        const value = config.getValue(event, t);
        if (value) {
          processedIssues.push({
            key: config.key,
            title: config.title(t),
            value,
            unitOfMeasure: config.getUnitOfMeasure?.(t),
            timing: config.timing,
            severity: config.severity || 'info',
          });
        }
      }
    });

    return processedIssues;
  }, [event, grouping, timing, t, visibleIssues, defaultVisibleIssues]);
};

/**
 * Hook to get processed stats for an event based on grouping and category
 */
export const useEventStats = (
  event: ActualityHqWithCongestionsRow,
  grouping: EventGrouping = 'all',
  category?: EventStatCategory,
  onlyVisible: boolean = false,
) => {
  const { t } = useTranslation('feed');

  return useMemo(() => {
    let statsConfig = category
      ? getStatsByCategory(category, grouping)
      : getStatsForGrouping(grouping);

    if (onlyVisible) {
      statsConfig = statsConfig.filter(stat => stat.defaultVisible);
    }

    const processedStats: ProcessedEventStat[] = [];

    statsConfig.forEach(config => {
      const value = config.getValue(event, t);
      if (value !== null && value !== undefined) {
        processedStats.push({
          key: config.key,
          title: config.title(t),
          value,
          unitOfMeasure: config.getUnitOfMeasure?.(t),
          category: config.category,
          defaultVisible: config.defaultVisible,
          canCopy: config.canCopy,
          truncate: config.truncate,
        });
      }
    });

    return processedStats;
  }, [event, grouping, category, onlyVisible, t]);
};

/**
 * Hook to get categorized stats for an event
 */
export const useEventStatsByCategory = (
  event: ActualityHqWithCongestionsRow,
  grouping: EventGrouping = 'all',
  onlyVisible: boolean = false,
) => {
  const { t } = useTranslation('feed');

  return useMemo(() => {
    const categories = getCategoriesForGrouping(grouping);
    const result: Record<EventStatCategory, ProcessedEventStat[]> = {} as any;

    categories.forEach(categoryConfig => {
      let statsConfig = getStatsByCategory(categoryConfig.key, grouping);

      if (onlyVisible) {
        statsConfig = statsConfig.filter(stat => stat.defaultVisible);
      }

      const processedStats: ProcessedEventStat[] = [];

      statsConfig.forEach(config => {
        const value = config.getValue(event, t);
        if (value !== null && value !== undefined) {
          processedStats.push({
            key: config.key,
            title: config.title(t),
            value,
            unitOfMeasure: config.getUnitOfMeasure?.(t),
            category: config.category,
            defaultVisible: config.defaultVisible,
            canCopy: config.canCopy,
            truncate: config.truncate,
          });
        }
      });

      if (processedStats.length > 0) {
        result[categoryConfig.key] = processedStats;
      }
    });

    return result;
  }, [event, grouping, onlyVisible, t]);
};

/**
 * Hook to get issues grouped by timing
 */
export const useEventIssuesByTiming = (
  event: ActualityHqWithCongestionsRow,
  grouping: EventGrouping = 'all',
  visibleIssues?: string[],
) => {
  const beforeIssues = useEventIssues(event, grouping, 'before', visibleIssues);
  const duringIssues = useEventIssues(event, grouping, 'during', visibleIssues);
  const afterIssues = useEventIssues(event, grouping, 'after', visibleIssues);

  return useMemo(
    () => ({
      before: beforeIssues,
      during: duringIssues,
      after: afterIssues,
      all: [...beforeIssues, ...duringIssues, ...afterIssues],
    }),
    [beforeIssues, duringIssues, afterIssues],
  );
};

/**
 * Hook to get primary and secondary stats based on grouping configuration
 */
export const useEventStatsGrouped = (
  event: ActualityHqWithCongestionsRow,
  grouping: EventGrouping = 'all',
) => {
  const { t } = useTranslation('feed');

  return useMemo(() => {
    const allStats = useEventStats(event, grouping);
    const statsMap = new Map(allStats.map(stat => [stat.key, stat]));

    const groupConfig = EVENT_GROUPING_CONFIG[grouping];

    const primaryStats = groupConfig.primaryStats
      .map(key => statsMap.get(key))
      .filter(Boolean) as ProcessedEventStat[];

    const secondaryStats = groupConfig.secondaryStats
      .map(key => statsMap.get(key))
      .filter(Boolean) as ProcessedEventStat[];

    return {
      primary: primaryStats,
      secondary: secondaryStats,
      all: allStats,
    };
  }, [event, grouping]);
};

/**
 * Hook to get configuration info for a specific grouping
 */
export const useEventGroupingConfig = (grouping: EventGrouping) => {
  const { t } = useTranslation('feed');

  return useMemo(() => {
    const config = EVENT_GROUPING_CONFIG[grouping];
    return {
      key: config.key,
      title: config.title(t),
      description: config.description(t),
      primaryStats: config.primaryStats,
      secondaryStats: config.secondaryStats,
      defaultIssues: config.defaultIssues,
    };
  }, [grouping, t]);
};

/**
 * Hook to get specific issue or stat configuration
 */
export const useEventConfigItem = (key: string, type: 'issue' | 'stat') => {
  const { t } = useTranslation('feed');

  return useMemo(() => {
    if (type === 'issue') {
      const config = EVENT_ISSUES_CONFIG.find(item => item.key === key);
      return config
        ? {
            ...config,
            title: config.title(t),
          }
        : null;
    } else {
      const config = EVENT_STATS_CONFIG.find(item => item.key === key);
      return config
        ? {
            ...config,
            title: config.title(t),
          }
        : null;
    }
  }, [key, type, t]);
};
